import React, {useEffect, useMemo} from 'react';
import {View, Text, TouchableOpacity, ActivityIndicator} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import {useSelector, useDispatch} from 'react-redux';
import {
  fetchUserZoneAccesses,
  updateUserZoneAccessOptional
} from '../../actions/zoneActions';
import {selectZoneAccessSource} from '../../selectors/scanPackSelectors';
import useWidth from '../scanToCartScanning/useWidth';
import styles, {
  ZONE_ACCESS_ACTIVE_GRADIENT_UP,
  ZONE_ACCESS_ACTIVE_GRADIENT_DOWN,
  ZONE_ACCESS_ACTIVE_BORDER_COLORS,
  ZONE_ACCESS_INACTIVE_GRADIENT_UP,
  ZONE_ACCESS_INACTIVE_GRADIENT_DOWN,
  ZONE_ACCESS_INACTIVE_BORDER_COLORS,
  ZONE_ACCESS_ALLOWED_TEXT_COLOR,
  ZONE_ACCESS_UNALLOWED_TEXT_COLOR
} from '../../style/scanpack';

export const OptionalZoneAccessButtons = ({username, maxWidth}) => {
  const dispatch = useDispatch();
  const screenWidth = useWidth();
  const effectiveMaxWidth = maxWidth != null ? maxWidth : screenWidth;
  const zoneAccessSource = useSelector(selectZoneAccessSource);
  const userZoneAccessesLoading = useSelector(
    state => state.zone.userZoneAccessesLoading
  );
  const userZoneAccesses = useSelector(state => state.zone.userZoneAccesses);

  const optionalZones = useMemo(() => {
    if (!userZoneAccesses || !Array.isArray(userZoneAccesses)) return [];
    return userZoneAccesses.filter(
      acc => acc.access_level === 'optional'
    );
  }, [userZoneAccesses]);

  useEffect(() => {
    if (zoneAccessSource !== 'user' || !username) {
      return;
    }
    dispatch(fetchUserZoneAccesses(username));
  }, [zoneAccessSource, username, dispatch]);

  const handleZoneButtonPress = (access) => {
    const isAllowed = access.optional_zone_access === true;
    const newValue = !isAllowed;
    dispatch(
      updateUserZoneAccessOptional(username, access.id, newValue, () => {
        dispatch(fetchUserZoneAccesses(username));
      })
    );
  };

  if (zoneAccessSource !== 'user') {
    return null;
  }

  if (userZoneAccessesLoading) {
    return (
      <View style={styles.zoneAccessButtonsContainer}>
        <ActivityIndicator size="small" color={ZONE_ACCESS_INACTIVE_GRADIENT_DOWN} />
      </View>
    );
  }

  if (optionalZones.length === 0) {
    return null;
  }

  return (
    <View style={styles.zoneAccessButtonsContainer}>
      <View style={[styles.zoneAccessButtonRow, effectiveMaxWidth != null ? {width: effectiveMaxWidth} : null]}>
        {optionalZones.map(access => {
          const zoneName = access.zone?.name ?? `Zone ${access.zone_id ?? ''}`;
          const isAllowed = access.optional_zone_access === true;
          const gradientColors = isAllowed
            ? [ZONE_ACCESS_ACTIVE_GRADIENT_UP, ZONE_ACCESS_ACTIVE_GRADIENT_DOWN]
            : [ZONE_ACCESS_INACTIVE_GRADIENT_UP, ZONE_ACCESS_INACTIVE_GRADIENT_DOWN];
          const borderColors = isAllowed
            ? ZONE_ACCESS_ACTIVE_BORDER_COLORS
            : ZONE_ACCESS_INACTIVE_BORDER_COLORS;
          const textColor = isAllowed
            ? ZONE_ACCESS_ALLOWED_TEXT_COLOR
            : ZONE_ACCESS_UNALLOWED_TEXT_COLOR;

          return (
            <View
              key={access.id ?? `${access.zone_id}-${access.zone?.id}`}
              style={styles.zoneAccessButtonWrapper}>
              <TouchableOpacity
                activeOpacity={0.8}
                onPress={() => handleZoneButtonPress(access)}
                testID={`optional-zone-${(zoneName || '').replace(/\s+/g, '-').toLowerCase()}`}>
                <LinearGradient
                  colors={borderColors}
                  style={styles.zoneAccessButtonBorder}
                  start={{x: 0.5, y: 0}}
                  end={{x: 0.5, y: 1}}>
                  <LinearGradient
                    colors={gradientColors}
                    style={styles.zoneAccessButtonInner}
                    start={{x: 0.5, y: 0}}
                    end={{x: 0.5, y: 1}}>
                    <Text style={[styles.zoneAccessButtonText, {color: textColor}]}>
                      {zoneName}
                    </Text>
                  </LinearGradient>
                </LinearGradient>
              </TouchableOpacity>
            </View>
          );
        })}
      </View>
    </View>
  );
};
